import { rawTimeZones } from '@vvo/tzdb'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'

dayjs.extend(utc)
dayjs.extend(timezone)

const validTimezones = new Set(['UTC'])

function isValidTimezone(zone: string | null | undefined): zone is string {
  if (!zone || zone === 'Etc/Unknown') return false
  if (validTimezones.has(zone)) return true

  try {
    new Intl.DateTimeFormat('en', { timeZone: zone }).format(0)
    validTimezones.add(zone)
    return true
  } catch {
    return false
  }
}

export function readBrowserTimezone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (isValidTimezone(zone)) return zone
  } catch {
    // Some browsers cannot map newly introduced Windows time-zone names.
  }

  return undefined
}

let cachedGuess: { signature: string; zone: string } | undefined

function guessBrowserTimezone(): string {
  const now = new Date()
  const year = now.getUTCFullYear()
  const dates = [now]
  for (const sampleYear of [year - 1, year, year + 1]) {
    dates.push(new Date(Date.UTC(sampleYear, 0, 15, 12)))
    dates.push(new Date(Date.UTC(sampleYear, 6, 15, 12)))
  }
  const offsets = dates.map((date) => date.getTimezoneOffset())
  const signature = `${year}:${offsets.join(',')}`
  if (cachedGuess?.signature === signature) return cachedGuess.zone

  // A guess, not an identification: compare seasonal offsets and let the
  // user correct it. Never infer a location from an IP address.
  let bestZone = 'UTC'
  let bestScore = Infinity
  for (const { name } of rawTimeZones) {
    try {
      const formatter = new Intl.DateTimeFormat('en', {
        timeZone: name,
        timeZoneName: 'longOffset'
      })
      let score = 0
      for (let index = 0; index < dates.length; index++) {
        const label = formatter
          .formatToParts(dates[index])
          .find((part) => part.type === 'timeZoneName')?.value
        const match = label?.match(/^GMT([+-])(\d{2}):(\d{2})$/)
        const offset =
          label === 'GMT'
            ? 0
            : match
              ? (Number(match[2]) * 60 + Number(match[3])) *
                (match[1] === '+' ? -1 : 1)
              : NaN
        score += Math.abs(offset - offsets[index])
      }
      if (score < bestScore) {
        bestZone = name
        bestScore = score
      }
    } catch {
      // The browser may not support every zone in the picker database.
    }
  }
  cachedGuess = { signature, zone: bestZone }
  return bestZone
}

export function getFallbackTimezone(): string {
  const saved = LocalStorage.get(LocalStorageKey.FALLBACK_TIMEZONE)
  return isValidTimezone(saved) ? saved : guessBrowserTimezone()
}

export function setFallbackTimezone(zone: string): boolean {
  if (!isValidTimezone(zone)) return false
  LocalStorage.set(LocalStorageKey.FALLBACK_TIMEZONE, zone)
  return true
}

export function getBrowserTimezone(): string {
  return readBrowserTimezone() ?? getFallbackTimezone()
}

// Workers receive the active zone in their message; they have no localStorage.
let globalTimezone =
  typeof window === 'undefined' ? 'UTC' : getBrowserTimezone()
dayjs.tz.setDefault(globalTimezone)

export function setDefaultTimezone(zone: string): void {
  globalTimezone = isValidTimezone(zone) ? zone : getBrowserTimezone()
  dayjs.tz.setDefault(globalTimezone)
  LocalStorage.set(LocalStorageKey.TIMEZONE, globalTimezone)
}

export function getDefaultTimezone(): string {
  // Match this tab's settings provider and Dayjs default. Storage is shared
  // with other tabs, which may have selected a different calendar zone.
  return globalTimezone
}

export function resolveTimezone(
  zone?: string | null,
  fallback = getDefaultTimezone()
): string {
  return isValidTimezone(zone)
    ? zone
    : isValidTimezone(fallback)
      ? fallback
      : getDefaultTimezone()
}

export function getTimezoneOffsetInSeconds(at: number = Date.now()): number {
  return dayjs(at).tz(getDefaultTimezone()).utcOffset() * 60
}

export function dateInTimezone(
  value: dayjs.ConfigType,
  zone = getDefaultTimezone()
) {
  const date = dayjs(value)
  const zoned = date.tz(zone)
  return zoned.utcOffset() === 0 ? date.utc() : zoned
}

// Calendar-day arithmetic stays independent of the computer zone and DST length.
export function appCalendarDate(daysFromToday = 0, at: number = Date.now()) {
  return dayjs
    .utc(dateInTimezone(at).format('YYYY-MM-DD'))
    .add(daysFromToday, 'day')
    .format('YYYY-MM-DD')
}

export function isAppToday(value: dayjs.ConfigType) {
  return dateInTimezone(value).format('YYYY-MM-DD') === appCalendarDate()
}
